// src/modules/agentic/ToolUseLab.jsx
// Tool Use Lab — function calling · schema design · parallel tools · error handling · tool selection

import React, { useState, useRef, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Panel, Metric, LogPanel, timestamp } from '../../components/ui.jsx';

const AC = '#4ade80';
const P  = ['#4ade80','#22d3ee','#a78bfa','#fbbf24','#f87171','#fb923c'];

const Btn = ({onClick,children,color=AC,disabled,style}) => (
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'22',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,opacity:disabled?.4:1,...style}}>{children}</button>
);
const Sl = ({label,value,min,max,step=1,onChange,unit='',color=AC}) => (
  <div style={{marginBottom:'10px'}}>
    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
      <span style={{fontSize:'10px',color:'var(--t2)'}}>{label}</span>
      <span style={{fontSize:'10px',color,fontWeight:700}}>{typeof value==='number'?value.toFixed(step<0.01?4:step<1?2:0):value}{unit}</span>
    </div>
    <div style={{position:'relative',height:'3px',background:'var(--b0)',borderRadius:'2px'}}>
      <div style={{position:'absolute',left:0,height:'100%',background:`linear-gradient(90deg,${color}44,${color})`,width:`${((value-min)/(max-min))*100}%`}}/>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(parseFloat(e.target.value))}
        style={{position:'absolute',inset:'-7px 0',width:'100%',opacity:0,cursor:'pointer',height:'18px'}}/>
    </div>
  </div>
);

// ─── TOOL DEFINITIONS ────────────────────────────────────────────────────────
const TOOLS = [
  {
    name: 'search_web',
    color: '#22d3ee',
    icon: '🔍',
    desc: 'Search the internet for current information',
    params: [
      {name:'query', type:'string', required:true, desc:'Search query'},
      {name:'num_results', type:'integer', required:false, desc:'Number of results (1-10)', default:5},
    ],
    latency: 800,
    failRate: 0.05,
    cost: 0.001,
  },
  {
    name: 'run_code',
    color: '#4ade80',
    icon: '⚡',
    desc: 'Execute Python code in a sandbox',
    params: [
      {name:'code', type:'string', required:true, desc:'Python code to execute'},
      {name:'timeout', type:'integer', required:false, desc:'Timeout in seconds', default:10},
    ],
    latency: 1200,
    failRate: 0.12,
    cost: 0.002,
  },
  {
    name: 'read_file',
    color: '#a78bfa',
    icon: '📄',
    desc: 'Read contents of a file',
    params: [
      {name:'path', type:'string', required:true, desc:'File path to read'},
      {name:'encoding', type:'string', required:false, desc:'File encoding', default:'utf-8'},
    ],
    latency: 80,
    failRate: 0.08,
    cost: 0.0001,
  },
  {
    name: 'write_file',
    color: '#fbbf24',
    icon: '✍',
    desc: 'Write or overwrite a file',
    params: [
      {name:'path', type:'string', required:true, desc:'Destination path'},
      {name:'content', type:'string', required:true, desc:'Content to write'},
      {name:'mode', type:'string', required:false, desc:'w=overwrite, a=append', default:'w'},
    ],
    latency: 90,
    failRate: 0.03,
    cost: 0.0001,
  },
  {
    name: 'query_database',
    color: '#fb923c',
    icon: '🗄',
    desc: 'Run a SQL query on the database',
    params: [
      {name:'sql', type:'string', required:true, desc:'SQL query string'},
      {name:'db_name', type:'string', required:true, desc:'Target database name'},
    ],
    latency: 200,
    failRate: 0.06,
    cost: 0.0005,
  },
  {
    name: 'send_email',
    color: '#f87171',
    icon: '📧',
    desc: 'Send an email via SMTP',
    params: [
      {name:'to', type:'string', required:true, desc:'Recipient email address'},
      {name:'subject', type:'string', required:true, desc:'Email subject line'},
      {name:'body', type:'string', required:true, desc:'Email body text'},
    ],
    latency: 600,
    failRate: 0.04,
    cost: 0.0002,
  },
];

// Fake tool execution
const execTool = (tool, params) => {
  const fail = Math.random() < tool.failRate;
  const results = {
    search_web: `Found 5 results for "${params.query||'query'}". Top result: Wikipedia article with 3 relevant sections.`,
    run_code: fail ? 'SyntaxError: unexpected EOF' : `Output:\n${params.code?.includes('print')?'Hello, World!':'42'}`,
    read_file: fail ? `FileNotFoundError: ${params.path}` : `File contents (${Math.floor(Math.random()*5000)} bytes): Lorem ipsum data...`,
    write_file: fail ? 'PermissionError: read-only filesystem' : `File written: ${params.path} (${params.content?.length||0} bytes)`,
    query_database: fail ? 'ConnectionError: database timeout' : `Query returned 12 rows in 0.003s`,
    send_email: fail ? 'SMTPError: connection refused' : `Email sent to ${params.to||'user@example.com'}`,
  };
  return { success: !fail, result: results[tool.name] || 'OK', latency: tool.latency + Math.random()*200 };
};

// ─── CALL FLOW SVG ────────────────────────────────────────────────────────────
function CallFlowSVG({ calls, parallel }) {
  const W=700, H=130, pad=20, rowH=30;
  if(!calls.length) return null;
  const maxT = Math.max(...calls.map(c=>c.end));
  const scaleX = x => pad + (x/maxT)*(W-pad*2);

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{background:'#060b14',borderRadius:'6px',border:'1px solid #1e293b',display:'block',marginBottom:8}}>
      <text x={pad} y={14} style={{fontSize:'8px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>{parallel?'PARALLEL':'SEQUENTIAL'} TOOL EXECUTION</text>
      {calls.map((c,i)=>{
        const y = parallel ? pad+18+i*rowH : pad+18;
        const x1=scaleX(c.start), x2=scaleX(c.end);
        return (
          <g key={i}>
            <rect x={x1} y={y} width={Math.max(4,x2-x1)} height={22} rx={3}
              fill={c.success?c.color+'33':' #f8717133'} stroke={c.success?c.color:'#f87171'} strokeWidth={1.5}/>
            <text x={x1+4} y={y+10} style={{fontSize:'7px',fill:c.success?c.color:'#f87171',fontFamily:'JetBrains Mono',fontWeight:700}}>{c.name}</text>
            <text x={x1+4} y={y+19} style={{fontSize:'6px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>{c.latency.toFixed(0)}ms</text>
          </g>
        );
      })}
      {/* Timeline axis */}
      <line x1={pad} y1={H-12} x2={W-pad} y2={H-12} stroke="#334155" strokeWidth={1}/>
      {[0,0.25,0.5,0.75,1].map(t=>(
        <text key={t} x={pad+(W-pad*2)*t} y={H-4} textAnchor="middle" style={{fontSize:'7px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>{(maxT*t).toFixed(0)}ms</text>
      ))}
    </svg>
  );
}

// ─── SCHEMA VIEWER ────────────────────────────────────────────────────────────
function SchemaView({ tool }) {
  const schema = {
    type: 'function',
    function: {
      name: tool.name,
      description: tool.desc,
      parameters: {
        type: 'object',
        properties: Object.fromEntries(tool.params.map(p=>([p.name,{type:p.type,description:p.desc,...(p.default!==undefined?{default:p.default}:{})}]))),
        required: tool.params.filter(p=>p.required).map(p=>p.name),
      }
    }
  };
  return (
    <pre style={{background:'#060b14',border:'1px solid #1e293b',borderRadius:'4px',padding:'12px',fontSize:'9px',color:'#94a3b8',fontFamily:'JetBrains Mono',overflow:'auto',maxHeight:200,margin:0,lineHeight:1.6}}>
      {JSON.stringify(schema, null, 2).replace(/"([^"]+)":/g, (m,k)=>`"<span style="color:#22d3ee">${k}</span>":`)
        .replace(/"(string|integer|object|boolean|array)"/g, '"<span style="color:#4ade80">$1</span>"')}
    </pre>
  );
}

const TABS = ['schemas','execution','parallel','error-handling','selection'];

export default function ToolUseLab() {
  const [tab, setTab] = useState('schemas');
  const [logs, setLogs] = useState([{ts:'00:00:00',msg:'Tool Use Lab — how agents call functions, handle failures, and parallelize work.',t:'info'}]);
  const log = (msg,t='info') => setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);

  const [selectedTool, setSelectedTool] = useState(TOOLS[0]);
  const [callLog, setCallLog] = useState([]);
  const [running, setRunning] = useState(false);
  const [parallel, setParallel] = useState(true);
  const [selectedTools, setSelectedTools] = useState(['search_web','run_code','read_file']);
  const [retries, setRetries] = useState(2);
  const [callFlowData, setCallFlowData] = useState([]);
  const [execParams, setExecParams] = useState({query:'latest AI news', code:'print("hello")', path:'/data/file.txt'});

  const runSelected = async () => {
    setRunning(true);
    setCallLog([]);
    const tools = TOOLS.filter(t=>selectedTools.includes(t.name));
    const calls = [];
    let cursor = 0;

    if(parallel) {
      // All start at 0
      tools.forEach(t => {
        const params = execParams;
        const r = execTool(t, params);
        const entry = {name:t.name, color:t.color, start:0, end:r.latency, latency:r.latency, success:r.success, result:r.result};
        calls.push(entry);
        cursor = Math.max(cursor, r.latency);
      });
    } else {
      let time=0;
      tools.forEach(t => {
        const params = execParams;
        const r = execTool(t, params);
        const entry = {name:t.name, color:t.color, start:time, end:time+r.latency, latency:r.latency, success:r.success, result:r.result};
        calls.push(entry);
        time += r.latency;
        cursor = time;
      });
    }

    setCallFlowData(calls);
    setCallLog(calls.map(c=>({...c, ts:timestamp()})));
    const totalTime = parallel ? Math.max(...calls.map(c=>c.end)) : calls.reduce((s,c)=>s+c.latency,0);
    const failures = calls.filter(c=>!c.success).length;
    log(`Executed ${calls.length} tools in ${totalTime.toFixed(0)}ms | ${failures} failed`,'ok');
    setRunning(false);
  };

  const toggleTool = (name) => {
    setSelectedTools(s => s.includes(name) ? s.filter(x=>x!==name) : [...s, name]);
  };

  // Error handling simulation
  const [errorScenario, setErrorScenario] = useState('timeout');
  const [retryResult, setRetryResult] = useState(null);

  const runErrorSim = () => {
    const scenarios = {
      timeout: {steps:['Tool call sent','Waiting...','Timeout after 10s','Retry 1/2 — increasing timeout','Timeout after 20s','Retry 2/2 — fallback to cached result','Returned stale cached data'],colors:['#22d3ee','#fbbf24','#f87171','#fb923c','#f87171','#fb923c','#4ade80']},
      malformed: {steps:['Tool call sent','Parsing response...','JSON parse error: unexpected token','Retry 1/2 — same call','JSON parse error again','Fallback: return raw string response','Partial result returned'],colors:['#22d3ee','#fbbf24','#f87171','#fb923c','#f87171','#fbbf24','#4ade80']},
      rate_limit: {steps:['Tool call sent','Rate limit hit: 429 Too Many Requests','Wait 5s (exponential backoff)','Retry 1/2 after 5s delay','Rate limit again: 429','Wait 10s (exponential backoff)','Retry 2/2 — success'],colors:['#22d3ee','#f87171','#fbbf24','#fb923c','#f87171','#fbbf24','#4ade80']},
      auth: {steps:['Tool call sent','Auth error: 401 Unauthorized','Refresh token attempt','New token acquired','Retry 1/2 with fresh token','Success — result returned'],colors:['#22d3ee','#f87171','#a78bfa','#4ade80','#fb923c','#4ade80']},
    };
    setRetryResult(scenarios[errorScenario]);
    log(`Error sim: ${errorScenario} — ${scenarios[errorScenario].steps.length} steps`,'ok');
  };

  // Tool selection scoring
  const taskQuery = 'Find the stock price of AAPL, run analysis code, save results to file';
  const selectionScores = TOOLS.map(t=>{
    const keywords = {search_web:['find','search','current','latest','what is'],run_code:['run','execute','code','analysis','compute','calculate'],read_file:['read','load','open','file'],write_file:['save','write','store','export','file'],query_database:['database','sql','query','records'],send_email:['email','send','notify','message']};
    const words = taskQuery.toLowerCase().split(/\s+/);
    const score = (keywords[t.name]||[]).filter(k=>words.some(w=>w.includes(k))).length;
    return {name:t.name, score, selected:score>0, color:t.color};
  }).sort((a,b)=>b.score-a.score);

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?AC:'transparent'}`,background:tab===t?AC+'18':'transparent',color:tab===t?AC:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* ── SCHEMAS ── */}
      {tab==='schemas'&&(
        <div style={{display:'grid',gridTemplateColumns:'200px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
            <div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'2px',padding:'0 2px'}}>SELECT TOOL</div>
            {TOOLS.map(t=>(
              <button key={t.name} onClick={()=>setSelectedTool(t)} style={{padding:'9px 10px',background:selectedTool.name===t.name?t.color+'18':'var(--deep)',border:`1px solid ${selectedTool.name===t.name?t.color:'var(--b0)'}`,color:selectedTool.name===t.name?t.color:'var(--t3)',fontSize:'9px',cursor:'pointer',borderRadius:'3px',textAlign:'left',fontFamily:'JetBrains Mono',fontWeight:selectedTool.name===t.name?700:400,display:'flex',alignItems:'center',gap:'7px'}}>
                <span>{t.icon}</span>{t.name}
              </button>
            ))}
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title={`${selectedTool.icon} ${selectedTool.name} — JSON SCHEMA`} accent={selectedTool.color}>
              <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px'}}>{selectedTool.desc}</div>
              <SchemaView tool={selectedTool}/>
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'8px',marginTop:'10px'}}>
                <Metric label="Avg latency" value={`${selectedTool.latency}ms`} color={selectedTool.color}/>
                <Metric label="Fail rate" value={`${(selectedTool.failRate*100).toFixed(0)}%`} color="#f87171"/>
                <Metric label="Cost/call" value={`$${selectedTool.cost}`} color="#fbbf24"/>
              </div>
            </Panel>
            <Panel title="PARAMETERS" accent="#22d3ee">
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'9px'}}>
                <thead><tr style={{background:'var(--deep)'}}>
                  {['Name','Type','Required','Default','Description'].map(h=><th key={h} style={{padding:'6px 8px',textAlign:'left',color:'var(--t3)',borderBottom:'1px solid var(--b0)'}}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {selectedTool.params.map(p=>(
                    <tr key={p.name} style={{borderBottom:'1px solid var(--b0)'}}>
                      <td style={{padding:'6px 8px',color:selectedTool.color,fontFamily:'JetBrains Mono',fontWeight:700}}>{p.name}</td>
                      <td style={{padding:'6px 8px',color:'#22d3ee',fontFamily:'JetBrains Mono'}}>{p.type}</td>
                      <td style={{padding:'6px 8px',color:p.required?'#f87171':'#64748b'}}>{p.required?'✓ yes':'optional'}</td>
                      <td style={{padding:'6px 8px',color:'#64748b',fontFamily:'JetBrains Mono'}}>{p.default??'—'}</td>
                      <td style={{padding:'6px 8px',color:'var(--t3)'}}>{p.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          </div>
        </div>
      )}

      {/* ── EXECUTION ── */}
      {tab==='execution'&&(
        <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="SELECT TOOLS" accent={AC}>
              {TOOLS.map(t=>(
                <button key={t.name} onClick={()=>toggleTool(t.name)} style={{display:'flex',alignItems:'center',gap:'8px',width:'100%',padding:'7px 8px',background:selectedTools.includes(t.name)?t.color+'18':'transparent',border:`1px solid ${selectedTools.includes(t.name)?t.color:'var(--b0)'}`,color:selectedTools.includes(t.name)?t.color:'var(--t3)',fontSize:'9px',cursor:'pointer',borderRadius:'3px',marginBottom:'4px',fontFamily:'JetBrains Mono',textAlign:'left'}}>
                  <span style={{fontSize:'12px'}}>{t.icon}</span>
                  <span style={{fontWeight:selectedTools.includes(t.name)?700:400}}>{t.name}</span>
                  {selectedTools.includes(t.name)&&<span style={{marginLeft:'auto',fontSize:'8px'}}>✓</span>}
                </button>
              ))}
              <Btn onClick={runSelected} disabled={running||!selectedTools.length} color={AC} style={{width:'100%',marginTop:'4px'}}>{running?'RUNNING…':'EXECUTE →'}</Btn>
            </Panel>
            <LogPanel logs={logs} height={100}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            {callFlowData.length>0&&<Panel title="EXECUTION TIMELINE" accent={AC}><CallFlowSVG calls={callFlowData} parallel={false}/></Panel>}
            {callLog.length>0&&(
              <Panel title="CALL RESULTS" accent="#22d3ee">
                {callLog.map((c,i)=>(
                  <div key={i} style={{padding:'8px 10px',background:'var(--deep)',borderRadius:'3px',marginBottom:'5px',borderLeft:`3px solid ${c.success?c.color:'#f87171'}`}}>
                    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}>
                      <span style={{fontSize:'10px',color:c.color,fontFamily:'JetBrains Mono',fontWeight:700}}>{c.name}</span>
                      <span style={{fontSize:'9px',color:c.success?'#4ade80':'#f87171',fontFamily:'JetBrains Mono'}}>{c.success?'✓ success':'✗ failed'} · {c.latency.toFixed(0)}ms</span>
                    </div>
                    <div style={{fontSize:'9px',color:'var(--t3)'}}>{c.result}</div>
                  </div>
                ))}
              </Panel>
            )}
            {!callLog.length&&<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'200px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Select tools and execute</div>}
          </div>
        </div>
      )}

      {/* ── PARALLEL ── */}
      {tab==='parallel'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:'12px'}}>
            <Panel title="PARALLEL CONFIG" accent="#fbbf24">
              <div style={{display:'flex',gap:'6px',marginBottom:'10px'}}>
                {[['parallel','PARALLEL'],['sequential','SEQUENTIAL']].map(([v,l])=>(
                  <button key={v} onClick={()=>setParallel(v==='parallel')} style={{flex:1,padding:'8px',border:`1px solid ${(v==='parallel')==parallel?'#fbbf24':'var(--b0)'}`,background:(v==='parallel')==parallel?'#fbbf2418':'transparent',color:(v==='parallel')==parallel?'#fbbf24':'var(--t3)',fontSize:'9px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:700}}>{l}</button>
                ))}
              </div>
              {TOOLS.map(t=>(
                <button key={t.name} onClick={()=>toggleTool(t.name)} style={{display:'flex',alignItems:'center',gap:'8px',width:'100%',padding:'7px 8px',background:selectedTools.includes(t.name)?t.color+'18':'transparent',border:`1px solid ${selectedTools.includes(t.name)?t.color:'var(--b0)'}`,color:selectedTools.includes(t.name)?t.color:'var(--t3)',fontSize:'9px',cursor:'pointer',borderRadius:'3px',marginBottom:'4px',fontFamily:'JetBrains Mono',textAlign:'left'}}>
                  <span>{t.icon}</span><span style={{fontWeight:selectedTools.includes(t.name)?700:400}}>{t.name}</span>
                  {selectedTools.includes(t.name)&&<span style={{marginLeft:'auto'}}>✓</span>}
                </button>
              ))}
              <Btn onClick={runSelected} disabled={running||!selectedTools.length} color="#fbbf24" style={{width:'100%',marginTop:'6px'}}>{running?'RUNNING…':'RUN →'}</Btn>
            </Panel>
            <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
              {callFlowData.length>0&&(
                <Panel title={`TIMELINE — ${parallel?'PARALLEL (all at once)':'SEQUENTIAL (one by one)'}`} accent="#fbbf24">
                  <CallFlowSVG calls={callFlowData} parallel={parallel}/>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'8px',marginTop:'8px'}}>
                    <Metric label={parallel?'Parallel time':'Sequential time'} value={parallel?`${Math.max(...callFlowData.map(c=>c.end)).toFixed(0)}ms`:`${callFlowData.reduce((s,c)=>s+c.latency,0).toFixed(0)}ms`} color="#fbbf24"/>
                    <Metric label="Speedup" value={parallel?`${(callFlowData.reduce((s,c)=>s+c.latency,0)/Math.max(...callFlowData.map(c=>c.end))).toFixed(1)}×`:'1.0×'} color="#4ade80"/>
                    <Metric label="Failures" value={callFlowData.filter(c=>!c.success).length} color="#f87171"/>
                  </div>
                </Panel>
              )}
              <Panel title="WHEN TO USE PARALLEL vs SEQUENTIAL" accent="#22d3ee">
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
                  <div>
                    <div style={{fontSize:'10px',color:'#4ade80',fontWeight:700,marginBottom:'6px'}}>✓ Use PARALLEL when</div>
                    {['Tools are independent (no data deps)','Latency matters more than order','Fetching from multiple sources at once','Multiple file reads / DB queries'].map((t,i)=>(
                      <div key={i} style={{fontSize:'9px',color:'var(--t3)',padding:'4px 8px',borderLeft:'2px solid #4ade8044',marginBottom:'3px'}}>{t}</div>
                    ))}
                  </div>
                  <div>
                    <div style={{fontSize:'10px',color:'#f87171',fontWeight:700,marginBottom:'6px'}}>✗ Use SEQUENTIAL when</div>
                    {['Tool B needs results from Tool A','Write-then-read dependencies','Order matters for side effects','Debugging requires step-by-step trace'].map((t,i)=>(
                      <div key={i} style={{fontSize:'9px',color:'var(--t3)',padding:'4px 8px',borderLeft:'2px solid #f8717144',marginBottom:'3px'}}>{t}</div>
                    ))}
                  </div>
                </div>
              </Panel>
            </div>
          </div>
        </div>
      )}

      {/* ── ERROR HANDLING ── */}
      {tab==='error-handling'&&(
        <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="ERROR SCENARIO" accent="#f87171">
              {[['timeout','⏱ Timeout'],['malformed','💥 Malformed response'],['rate_limit','🚦 Rate limit'],['auth','🔑 Auth failure']].map(([v,l])=>(
                <button key={v} onClick={()=>setErrorScenario(v)} style={{display:'block',width:'100%',padding:'8px',background:errorScenario===v?'#f8717118':'var(--deep)',border:`1px solid ${errorScenario===v?'#f87171':'var(--b0)'}`,color:errorScenario===v?'#f87171':'var(--t3)',fontSize:'9px',cursor:'pointer',borderRadius:'3px',textAlign:'left',marginBottom:'4px',fontFamily:'JetBrains Mono',fontWeight:errorScenario===v?700:400}}>{l}</button>
              ))}
              <Btn onClick={runErrorSim} color="#f87171" style={{width:'100%',marginTop:'4px'}}>SIMULATE →</Btn>
            </Panel>
            <Panel title="RETRY POLICY" accent="#fbbf24">
              <Sl label="Max retries" value={retries} min={0} max={5} step={1} onChange={setRetries} color="#fbbf24"/>
              <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.7}}>
                <div style={{color:'#fbbf24',marginBottom:'3px',fontWeight:700}}>Backoff: exponential</div>
                {Array.from({length:retries+1},(_,i)=><div key={i}>Attempt {i+1}: wait {Math.pow(2,i)}s</div>)}
              </div>
            </Panel>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            {retryResult ? (
              <Panel title={`ERROR HANDLING TRACE — ${errorScenario.replace('_',' ').toUpperCase()}`} accent="#f87171">
                <div style={{display:'flex',flexDirection:'column',gap:'4px'}}>
                  {retryResult.steps.map((s,i)=>(
                    <div key={i} style={{display:'flex',alignItems:'center',gap:'10px',padding:'8px 12px',background:'var(--deep)',borderRadius:'3px',borderLeft:`3px solid ${retryResult.colors[i]}`}}>
                      <span style={{fontSize:'9px',color:'var(--t3)',fontFamily:'JetBrains Mono',minWidth:20}}>{i+1}</span>
                      <span style={{fontSize:'10px',color:retryResult.colors[i]}}>{s}</span>
                    </div>
                  ))}
                </div>
              </Panel>
            ):<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'200px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Select scenario and simulate</div>}
            <Panel title="ERROR HANDLING BEST PRACTICES" accent="#fbbf24">
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px'}}>
                {[
                  {p:'Always validate params BEFORE calling','c':'#4ade80'},
                  {p:'Return structured errors (code + message)','c':'#22d3ee'},
                  {p:'Exponential backoff for rate limits','c':'#fbbf24'},
                  {p:'Fallback tools for critical operations','c':'#a78bfa'},
                  {p:'Log all tool calls + results for debugging','c':'#fb923c'},
                  {p:'Set hard timeouts — never let agent hang','c':'#f87171'},
                ].map(({p,c},i)=>(
                  <div key={i} style={{fontSize:'9px',color:'var(--t3)',padding:'6px 8px',background:'var(--deep)',borderRadius:'2px',borderLeft:`2px solid ${c}`}}>{p}</div>
                ))}
              </div>
            </Panel>
          </div>
        </div>
      )}

      {/* ── SELECTION ── */}
      {tab==='selection'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="TOOL SELECTION — HOW LLM CHOOSES WHICH TOOL TO CALL" accent="#a78bfa">
            <div style={{padding:'10px 12px',background:'var(--deep)',borderRadius:'3px',marginBottom:'10px',borderLeft:'3px solid #a78bfa'}}>
              <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'3px'}}>TASK:</div>
              <div style={{fontSize:'11px',color:'#a78bfa',fontFamily:'JetBrains Mono'}}>{taskQuery}</div>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:'5px',marginBottom:'10px'}}>
              {selectionScores.map((t,i)=>(
                <div key={t.name} style={{display:'flex',alignItems:'center',gap:'10px',padding:'8px 10px',background:'var(--deep)',borderRadius:'3px',border:`1px solid ${t.selected?t.color+'44':'var(--b0)'}`,opacity:t.selected?1:0.5}}>
                  <span style={{fontSize:'8px',color:'var(--t3)',minWidth:16,fontFamily:'JetBrains Mono'}}>{i+1}</span>
                  <span style={{fontSize:'10px',color:t.color,fontFamily:'JetBrains Mono',fontWeight:700,minWidth:130}}>{t.name}</span>
                  <div style={{flex:1,height:12,background:'var(--b0)',borderRadius:2,overflow:'hidden'}}>
                    <div style={{height:'100%',width:`${t.score>0?(t.score/3)*100:5}%`,background:t.color+'88',borderRadius:2}}/>
                  </div>
                  <span style={{fontSize:'9px',color:t.selected?t.color:'var(--t3)',fontFamily:'JetBrains Mono',minWidth:60,textAlign:'right'}}>{t.selected?`score: ${t.score}`:'not needed'}</span>
                  {t.selected&&<span style={{fontSize:'9px',color:'#4ade80',fontWeight:700}}>✓ CALL</span>}
                </div>
              ))}
            </div>
          </Panel>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
            <Panel title="HOW LLM PICKS TOOLS" accent="#22d3ee">
              {[
                {step:'1. Read tool descriptions',detail:'LLM reads all tool schemas injected in system prompt'},
                {step:'2. Match task to tools',detail:'Semantic matching of task keywords to tool descriptions'},
                {step:'3. Check dependencies',detail:'Does Tool B need output from Tool A? (→ sequential)'},
                {step:'4. Decide: call or not?',detail:'If no tool fits, LLM answers from knowledge directly'},
                {step:'5. Fill parameters',detail:'Extract slot values from user message or prior context'},
              ].map(({step,detail},i)=>(
                <div key={i} style={{display:'flex',gap:'10px',marginBottom:'6px',padding:'6px 8px',background:'var(--deep)',borderRadius:'2px'}}>
                  <span style={{fontSize:'9px',color:'#22d3ee',fontFamily:'JetBrains Mono',fontWeight:700,minWidth:140}}>{step}</span>
                  <span style={{fontSize:'9px',color:'var(--t3)'}}>{detail}</span>
                </div>
              ))}
            </Panel>
            <Panel title="TOOL DESIGN RULES" accent="#4ade80">
              {[
                {rule:'Short, action-verb names',ex:'search_web ✓ vs doSearchOnTheInternet ✗'},
                {rule:'One job per tool',ex:'read_file not read_and_parse_and_summarize_file'},
                {rule:'Explicit required vs optional',ex:'Mark params that can be omitted'},
                {rule:'Descriptive descriptions',ex:'Include examples in desc: "query: \'AAPL stock price\'"'},
                {rule:'Return structured output',ex:'JSON with success + data + error fields'},
              ].map(({rule,ex},i)=>(
                <div key={i} style={{marginBottom:'6px',padding:'6px 8px',background:'var(--deep)',borderRadius:'2px',borderLeft:'2px solid #4ade80'}}>
                  <div style={{fontSize:'9px',color:'#4ade80',fontWeight:700,marginBottom:'2px'}}>{rule}</div>
                  <div style={{fontSize:'8px',color:'var(--t3)'}}>{ex}</div>
                </div>
              ))}
            </Panel>
          </div>
          <LogPanel logs={logs} height={70}/>
        </div>
      )}
    </div>
  );
}
